import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, addDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import { db, storage } from '../../firebase';
import { DocumentIcon, ArrowUpTrayIcon, CheckCircleIcon, XMarkIcon, UserIcon, BuildingOfficeIcon } from '@heroicons/react/20/solid';

// Required for term sheet (latest 3 years + interim financials)
const REQUIRED_DOCS = [
  { value: 'Personal Tax Return 2025', label: 'Personal Tax Return 2025' },
  { value: 'Personal Tax Return 2024', label: 'Personal Tax Return 2024' },
  { value: 'Personal Tax Return 2023', label: 'Personal Tax Return 2023' },
  { value: 'Personal Financial Statement', label: 'Personal Financial Statement (PFS)' },
  { value: 'Resume', label: 'Resume / CV' },
  { value: 'Business Tax Return 2025', label: 'Business Tax Return 2025' },
  { value: 'Business Tax Return 2024', label: 'Business Tax Return 2024' },
  { value: 'Business Tax Return 2023', label: 'Business Tax Return 2023' },
  { value: 'Interim P&L 2026 YTD', label: 'Interim P&L 2026 (YTD)' },
  { value: 'Trailing 12 Months P&L', label: 'Trailing 12 Months P&L' },
  { value: 'LOI', label: 'Letter of Intent (LOI)' },
  { value: 'Balance Sheet', label: 'Balance Sheet' },
];

// Only required when 2025 returns are on extension
const EXTENSION_DOCS = [
  { value: 'Personal Tax Return 2022', label: 'Personal Tax Return 2022' },
  { value: 'Business Tax Return 2022', label: 'Business Tax Return 2022' },
];

// Required when the buyer is a business acquiring another business
const ACQUIRING_BUSINESS_DOCS = [
  { value: 'Acquiring Business Tax Return 2025', label: 'Acquiring Co. Tax Return 2025' },
  { value: 'Acquiring Business Tax Return 2024', label: 'Acquiring Co. Tax Return 2024' },
  { value: 'Acquiring Business Tax Return 2023', label: 'Acquiring Co. Tax Return 2023' },
  { value: 'Acquiring Business Interim P&L 2026 YTD', label: 'Acquiring Co. Interim P&L 2026 (YTD)' },
  { value: 'Acquiring Business P&L', label: 'Acquiring Co. P&L' },
  { value: 'Acquiring Business Balance Sheet', label: 'Acquiring Co. Balance Sheet' },
];

// Required when real estate is part of the deal
const REAL_ESTATE_DOCS = [
  { value: 'Real Estate Valuation', label: 'Real Estate Valuation / Appraisal' },
];

// Extra / supplemental files
const EXTRA_DOCS = [
  { value: 'CIM', label: 'Confidential Information Memorandum (CIM)' },
  { value: 'Debt Schedule', label: 'Debt Schedule' },
  { value: 'Articles of Organization', label: 'Articles of Organization' },
  { value: 'Operating Agreement', label: 'Operating Agreement' },
  { value: 'AR Aging', label: 'AR Aging' },
  { value: 'AP Aging', label: 'AP Aging' },
  { value: 'Bank Statements', label: 'Bank Statements' },
  { value: 'Business Valuation', label: 'Business Valuation' },
  { value: 'Lease Agreement', label: 'Lease Agreement' },
  { value: 'Franchise Agreement', label: 'Franchise Agreement' },
  { value: 'Other', label: 'Other — name it yourself' },
];

const DOC_TYPES = [
  { category: 'Required for Term Sheet', items: REQUIRED_DOCS },
  { category: 'Acquiring Company', items: ACQUIRING_BUSINESS_DOCS },
  { category: 'Real Estate', items: REAL_ESTATE_DOCS },
  { category: 'If 2025 on Extension', items: EXTENSION_DOCS },
  { category: 'Extra Files', items: EXTRA_DOCS },
];

// Best-effort guess of doc type from a filename — used for bulk upload
const guessDocType = (filename) => {
  const n = filename.toLowerCase();
  const all = [...REQUIRED_DOCS, ...EXTENSION_DOCS, ...EXTRA_DOCS];
  const yearMatch = n.match(/20(2[2-6])/);
  const year = yearMatch ? `20${yearMatch[1]}` : null;
  if (/p[\s_-]*&?[\s_-]*l|profit[\s_-]*and[\s_-]*loss|income[\s_-]*statement/.test(n)) {
    if (n.includes('trailing') || n.includes('ttm') || n.includes('t12')) return 'Trailing 12 Months P&L';
    if (year === '2026' || n.includes('ytd') || n.includes('interim')) return 'Interim P&L 2026 YTD';
  }
  if (/personal[\s_-]*tax|1040|individual[\s_-]*tax/.test(n) && year) return `Personal Tax Return ${year}`;
  if (/(business|biz|corporate|corp|1120|1065)[\s_-]*tax|tax[\s_-]*return/.test(n) && year) return `Business Tax Return ${year}`;
  if (/pfs|personal[\s_-]*financial[\s_-]*statement/.test(n)) return 'Personal Financial Statement';
  if (/resume|cv\b/.test(n)) return 'Resume';
  if (/loi|letter[\s_-]*of[\s_-]*intent/.test(n)) return 'LOI';
  if (/balance[\s_-]*sheet/.test(n)) return 'Balance Sheet';
  if (/cim|memorandum/.test(n)) return 'CIM';
  if (/debt[\s_-]*schedule/.test(n)) return 'Debt Schedule';
  if (/articles/.test(n)) return 'Articles of Organization';
  if (/operating[\s_-]*agreement/.test(n)) return 'Operating Agreement';
  if (/\bar[\s_-]*aging\b/.test(n)) return 'AR Aging';
  if (/\bap[\s_-]*aging\b/.test(n)) return 'AP Aging';
  if (/bank[\s_-]*statement/.test(n)) return 'Bank Statements';
  if (/valuation/.test(n)) return 'Business Valuation';
  if (/lease/.test(n)) return 'Lease Agreement';
  if (/franchise/.test(n)) return 'Franchise Agreement';
  const direct = all.find(d => n.includes(d.value.toLowerCase()));
  return direct ? direct.value : '';
};

export default function BrokerDealUpload() {
  const { brokerId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const existingDealId = searchParams.get('dealId');

  // Step 1: Buyer + business info form
  const [step, setStep] = useState('info'); // 'info' | 'upload'
  const [buyerFirstName, setBuyerFirstName] = useState('');
  const [buyerLastName, setBuyerLastName] = useState('');
  const [buyerEmail, setBuyerEmail] = useState('');
  const [buyerPhone, setBuyerPhone] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [purchasePrice, setPurchasePrice] = useState('');
  const [businessNotes, setBusinessNotes] = useState('');
  const [contactPreference, setContactPreference] = useState('lended'); // 'lended' = we contact, 'broker' = you contact
  const [creatingDeal, setCreatingDeal] = useState(false);

  // Step 2: Document uploads
  const [loanAppId, setLoanAppId] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedDocType, setSelectedDocType] = useState('');
  const [customLabel, setCustomLabel] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');
  const [onExtension2025, setOnExtension2025] = useState(false);
  const [isBusinessAcquiring, setIsBusinessAcquiring] = useState(false);
  const [realEstateIncluded, setRealEstateIncluded] = useState(false);
  const [realEstateValue, setRealEstateValue] = useState('');
  const [bulkQueue, setBulkQueue] = useState([]); // [{ file, label, customLabel, status, error }]
  const [bulkUploading, setBulkUploading] = useState(false);

  // Broker info
  const [brokerData, setBrokerData] = useState(null);
  const [submitterInfo, setSubmitterInfo] = useState(null); // { uid, name } of the logged-in team member, if any
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBroker = async () => {
      try {
        const brokerSnap = await getDoc(doc(db, 'brokers', brokerId));
        if (brokerSnap.exists()) {
          setBrokerData({ id: brokerSnap.id, ...brokerSnap.data() });
        } else {
          setError('Broker not found');
        }
      } catch (err) {
        setError('Error loading broker data');
      }
      setLoading(false);
    };
    fetchBroker();
  }, [brokerId]);

  // Identify the logged-in user (team member or parent broker) submitting the deal
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) { setSubmitterInfo(null); return; }
      try {
        const snap = await getDoc(doc(db, 'brokers', user.uid));
        const data = snap.exists() ? snap.data() : {};
        const name = `${data.firstName || ''} ${data.lastName || ''}`.trim()
          || data.brokerAgeName
          || data.name
          || user.email
          || 'Team Member';
        setSubmitterInfo({ uid: user.uid, name });
      } catch (err) {
        setSubmitterInfo({ uid: user.uid, name: user.email || 'Team Member' });
      }
    });
    return () => unsubscribe();
  }, []);

  // If a dealId is passed, hydrate from the existing loanApplication and jump straight to the upload step
  useEffect(() => {
    if (!existingDealId) return;
    const loadExistingDeal = async () => {
      try {
        const dealSnap = await getDoc(doc(db, 'loanApplications', existingDealId));
        if (!dealSnap.exists()) {
          setError('Deal not found');
          return;
        }
        const d = dealSnap.data();
        setBuyerFirstName(d.firstName || '');
        setBuyerLastName(d.lastName || '');
        setBuyerEmail(d.email || '');
        setBuyerPhone(d.phoneNumber || '');
        setBusinessName(d.businessName || '');
        setPurchasePrice(d.purchasePrice ? String(d.purchasePrice) : '');
        setBusinessNotes(d.businessDescription || d.notes || '');
        setContactPreference(d.contactPreference || 'lended');
        setOnExtension2025(!!d.onExtension2025);
        setIsBusinessAcquiring(!!d.isBusinessAcquiring);
        setRealEstateIncluded(!!d.realEstateIncluded);
        setRealEstateValue(d.realEstateValue ? String(d.realEstateValue) : '');
        setLoanAppId(existingDealId);

        const filesSnap = await getDocs(query(collection(db, 'documentMetadata'), where('loanApplicationId', '==', existingDealId)));
        setUploadedFiles(filesSnap.docs.map(fd => ({ id: fd.id, ...fd.data() })));
        setStep('upload');
      } catch (err) {
        console.error('Error loading existing deal:', err);
        setError('Error loading deal');
      }
    };
    loadExistingDeal();
  }, [existingDealId]);

  const handleCreateDeal = async () => {
    if (!buyerFirstName || !buyerLastName || !buyerEmail || !buyerPhone || !businessNotes.trim()) {
      setError('First name, last name, email, phone, and business description are required');
      return;
    }
    setCreatingDeal(true);
    setError('');

    try {
      const brokerName = brokerData
        ? (brokerData.firstName && brokerData.lastName
          ? `${brokerData.firstName} ${brokerData.lastName}`
          : brokerData.brokerAgeName || brokerData.name || 'Broker')
        : 'Broker';

      const loanRef = await addDoc(collection(db, 'loanApplications'), {
        firstName: buyerFirstName.trim(),
        lastName: buyerLastName.trim(),
        email: buyerEmail.toLowerCase().trim(),
        phoneNumber: buyerPhone,
        status: 'Pre-lender',
        loanPurpose: 'Acquisition',
        businessName: businessName || '',
        purchasePrice: purchasePrice ? Number(purchasePrice) : null,
        notes: businessNotes,
        businessDescription: businessNotes,
        onExtension2025,
        isBusinessAcquiring,
        realEstateIncluded,
        realEstateValue: realEstateIncluded && realEstateValue ? Number(realEstateValue) : null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastUserUpdate: serverTimestamp(),
        personalGuarantors: [{ firstName: buyerFirstName.trim(), lastName: buyerLastName.trim() }],
        source: 'broker_deal',
        brokerId: brokerId,
        brokerName: brokerName,
        submittedBy: submitterInfo || null,
        sharedUploadRoute: true,
        called: false,
        allDocumentsUploaded: false,
        contactPreference: contactPreference,
      });

      setLoanAppId(loanRef.id);
      setStep('upload');

      // Notify Max + Ben about the new deal
      await addDoc(collection(db, 'email'), {
        to: ['max@lended.ai', 'ben@lended.ai', 'isak@lended.ai'],
        from: 'Lended Bot <isak@lended.ai>',
        subject: `[Broker Deal] ${buyerFirstName} ${buyerLastName} — ${businessName || 'New Deal'} from ${brokerName}`,
        message: {
          subject: `[Broker Deal] ${buyerFirstName} ${buyerLastName} — ${businessName || 'New Deal'} from ${brokerName}`,
          html: `<p>New full deal submitted by broker <strong>${brokerName}</strong>:</p>
<ul>
<li>Buyer: ${buyerFirstName} ${buyerLastName} (${buyerEmail})</li>
<li>Phone: ${buyerPhone || '—'}</li>
<li>Business: ${businessName || '—'}</li>
<li>Purchase Price: ${purchasePrice ? '$' + Number(purchasePrice).toLocaleString() : '—'}</li>
<li>Notes: ${businessNotes || '—'}</li>
<li>Contact preference: <strong>${contactPreference === 'lended' ? 'Lended contacts buyer' : 'Broker contacts buyer'}</strong></li>
<li>Submitted by: <strong>${submitterInfo?.name || brokerName}</strong></li>
</ul>
<p>Loan Application ID: ${loanRef.id}</p>`,
        },
      });
    } catch (err) {
      setError('Error creating deal: ' + err.message);
    }
    setCreatingDeal(false);
  };

  // Uploads a single file with the given label and returns the saved metadata.
  // onProgress(percent) is optional.
  const performUpload = (file, label, onProgress) => new Promise((resolve, reject) => {
    const storagePath = `broker-deals/${brokerId}/${loanAppId}/${label}/${file.name}`;
    const storageRef = ref(storage, storagePath);
    const uploadTask = uploadBytesResumable(storageRef, file);
    uploadTask.on('state_changed',
      (snapshot) => {
        if (onProgress) onProgress(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100));
      },
      (err) => reject(err),
      async () => {
        try {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          const metadata = {
            documentName: label,
            label,
            fileName: file.name,
            fileUrl: downloadURL,
            uploadedAt: new Date().toISOString(),
            uploadLinkId: loanAppId,
            loanApplicationId: loanAppId,
            email: buyerEmail.toLowerCase().trim(),
            uploadedBy: 'broker',
            brokerId,
            storagePath,
          };
          const docRef = await addDoc(collection(db, 'documentMetadata'), metadata);
          await updateDoc(doc(db, 'loanApplications', loanAppId), {
            lastUserUpdate: serverTimestamp(),
            updatedAt: serverTimestamp(),
            onExtension2025,
            isBusinessAcquiring,
            realEstateIncluded,
            realEstateValue: realEstateIncluded && realEstateValue ? Number(realEstateValue) : null,
          });
          resolve({ id: docRef.id, ...metadata });
        } catch (err) { reject(err); }
      }
    );
  });

  const handleUpload = async () => {
    if (!selectedFile) return;
    const label = (customLabel && customLabel.trim()) || (selectedDocType === 'Other' ? '' : selectedDocType);
    if (!label) return;
    setUploading(true);
    setUploadProgress(0);
    setSuccessMessage('');
    setError('');
    try {
      const saved = await performUpload(selectedFile, label, setUploadProgress);
      setUploadedFiles(prev => [...prev, saved]);
      setSuccessMessage(`${label} uploaded successfully!`);
      setSelectedFile(null);
      setSelectedDocType('');
      setCustomLabel('');
      const fileInput = document.getElementById('broker-file-upload');
      if (fileInput) fileInput.value = '';
    } catch (err) {
      setError('Upload failed: ' + (err.message || 'unknown error'));
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // ---- Bulk upload helpers ----
  const handleBulkFilesPicked = (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);
    setBulkQueue(files.map(file => ({
      file,
      label: guessDocType(file.name),
      customLabel: '',
      status: 'pending', // 'pending' | 'uploading' | 'done' | 'error'
      error: '',
    })));
    setError('');
    setSuccessMessage('');
  };

  const updateBulkRow = (idx, patch) => {
    setBulkQueue(prev => prev.map((row, i) => i === idx ? { ...row, ...patch } : row));
  };

  const removeBulkRow = (idx) => {
    setBulkQueue(prev => prev.filter((_, i) => i !== idx));
  };

  const startBulkUpload = async () => {
    setBulkUploading(true);
    setError('');
    const queue = bulkQueue;
    for (let i = 0; i < queue.length; i++) {
      const row = queue[i];
      if (row.status === 'done') continue;
      const label = (row.customLabel && row.customLabel.trim())
        || (row.label === 'Other' ? '' : row.label);
      if (!label) { updateBulkRow(i, { status: 'error', error: 'Pick a doc type or give it a custom name' }); continue; }
      updateBulkRow(i, { status: 'uploading', error: '' });
      try {
        const saved = await performUpload(row.file, label);
        setUploadedFiles(prev => [...prev, saved]);
        updateBulkRow(i, { status: 'done' });
      } catch (err) {
        updateBulkRow(i, { status: 'error', error: err.message || 'Upload failed' });
      }
    }
    setBulkUploading(false);
  };

  const closeBulkModal = () => {
    if (bulkUploading) return;
    setBulkQueue([]);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error && !brokerData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded-xl shadow-lg text-center max-w-md">
          <XMarkIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Error</h2>
          <p className="text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  // Step 1: Buyer + Business Info
  if (step === 'info') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-indigo-100 mb-4">
              <BuildingOfficeIcon className="h-8 w-8 text-indigo-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Submit Full Deal</h1>
            <p className="text-gray-500 mt-1">Enter the buyer and business details, then upload all required documents.</p>
            <button
              onClick={() => window.close() || window.history.back()}
              className="mt-3 px-4 py-1.5 text-sm text-gray-500 hover:text-gray-700"
            >
              ← Back to Portal
            </button>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 space-y-6">
            {/* Buyer Info */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
                <UserIcon className="h-5 w-5 text-indigo-600" />
                Buyer Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                  <input type="text" value={buyerFirstName} onChange={(e) => setBuyerFirstName(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                  <input type="text" value={buyerLastName} onChange={(e) => setBuyerLastName(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input type="email" value={buyerEmail} onChange={(e) => setBuyerEmail(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                  <input type="tel" value={buyerPhone} onChange={(e) => setBuyerPhone(e.target.value)}
                    placeholder="(555) 555-5555"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                </div>
              </div>

              {/* Contact preference toggle */}
              <div className="mt-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">Who contacts the buyer?</label>
                <div className="relative inline-flex w-full max-w-md p-1 bg-gray-100 rounded-xl">
                  <span
                    className="absolute top-1 bottom-1 left-1 bg-white rounded-lg shadow-sm transition-transform duration-200 ease-out"
                    style={{
                      width: 'calc(50% - 4px)',
                      transform: contactPreference === 'lended' ? 'translateX(0)' : 'translateX(100%)',
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setContactPreference('lended')}
                    className={`relative flex-1 z-10 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                      contactPreference === 'lended' ? 'text-indigo-700' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    We contact
                  </button>
                  <button
                    type="button"
                    onClick={() => setContactPreference('broker')}
                    className={`relative flex-1 z-10 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                      contactPreference === 'broker' ? 'text-indigo-700' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    You contact
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {contactPreference === 'lended'
                    ? "Lended will reach out to the buyer directly."
                    : "You'll handle outreach — Lended will stay behind the scenes."}
                </p>
              </div>
            </div>

            {/* Business Info */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
                <BuildingOfficeIcon className="h-5 w-5 text-indigo-600" />
                Business Details
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Business Name</label>
                  <input type="text" value={businessName} onChange={(e) => setBusinessName(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Price</label>
                  <input type="number" value={purchasePrice} onChange={(e) => setPurchasePrice(e.target.value)}
                    placeholder="e.g. 2000000"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Business Description *</label>
                <textarea value={businessNotes} onChange={(e) => setBusinessNotes(e.target.value)}
                  rows={4} placeholder="What does the business do? Years in operation, location, key customers, what's driving the sale, anything else we should know."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
              </div>
            </div>

            {/* Deal Options */}
            <div className="border-t pt-5">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Deal Options</h3>
              <p className="text-xs text-gray-500 mb-3">These determine which documents we'll need on the next step.</p>
              <div className="space-y-3">
                <label className="flex items-start gap-2 cursor-pointer p-3 rounded-lg border border-gray-200 hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={onExtension2025}
                    onChange={(e) => setOnExtension2025(e.target.checked)}
                    className="mt-0.5 h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-700">
                    <span className="font-medium">2025 returns are on extension</span>
                    <span className="block text-xs text-gray-500 mt-0.5">Adds 2022 personal &amp; business tax returns to the required list.</span>
                  </span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer p-3 rounded-lg border border-gray-200 hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={isBusinessAcquiring}
                    onChange={(e) => setIsBusinessAcquiring(e.target.checked)}
                    className="mt-0.5 h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-700">
                    <span className="font-medium">A business is acquiring another business</span>
                    <span className="block text-xs text-gray-500 mt-0.5">Adds the acquiring company's 2023–2025 returns, 2026 YTD interim, P&amp;L, and balance sheet.</span>
                  </span>
                </label>
                <div className={`rounded-lg border ${realEstateIncluded ? 'border-indigo-200 bg-indigo-50/40' : 'border-gray-200'}`}>
                  <label className="flex items-start gap-2 cursor-pointer p-3 hover:bg-gray-50 rounded-lg">
                    <input
                      type="checkbox"
                      checked={realEstateIncluded}
                      onChange={(e) => setRealEstateIncluded(e.target.checked)}
                      className="mt-0.5 h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-700">
                      <span className="font-medium">Real estate is part of the deal</span>
                      <span className="block text-xs text-gray-500 mt-0.5">Adds a Real Estate Valuation / Appraisal doc to the required list.</span>
                    </span>
                  </label>
                  {realEstateIncluded && (
                    <div className="px-3 pb-3 pl-9">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Estimated real estate value</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">$</span>
                        <input
                          type="number"
                          value={realEstateValue}
                          onChange={(e) => setRealEstateValue(e.target.value)}
                          placeholder="e.g. 500000"
                          className="w-full pl-7 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}

            <button
              onClick={handleCreateDeal}
              disabled={creatingDeal || !buyerFirstName || !buyerLastName || !buyerEmail || !buyerPhone || !businessNotes.trim()}
              className="w-full py-3 px-4 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {creatingDeal ? 'Creating Deal...' : 'Continue to Document Upload'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: Document Uploads
  const uploadedLabels = uploadedFiles.map(f => f.label || f.documentName);
  const activeRequiredDocs = [
    ...REQUIRED_DOCS,
    ...(onExtension2025 ? EXTENSION_DOCS : []),
    ...(isBusinessAcquiring ? ACQUIRING_BUSINESS_DOCS : []),
    ...(realEstateIncluded ? REAL_ESTATE_DOCS : []),
  ];
  const requiredUploaded = activeRequiredDocs.filter(d => uploadedLabels.includes(d.value)).length;
  const requiredComplete = requiredUploaded === activeRequiredDocs.length;
  const extraUploaded = EXTRA_DOCS.filter(d => d.value !== 'Other' && uploadedLabels.includes(d.value)).length;

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Upload Deal Documents</h1>
            <p className="text-gray-500 mt-1">{buyerFirstName} {buyerLastName} — {businessName || 'New Deal'}</p>
          </div>
          <button
            onClick={() => window.close() || window.history.back()}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Exit
          </button>
        </div>

        {/* Bulk upload CTA */}
        <div className="bg-white rounded-xl shadow-lg p-5 mb-6 flex items-center justify-between gap-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Have a bunch of files?</h3>
            <p className="text-xs text-gray-500 mt-0.5">Upload multiple files or a whole folder at once — we'll let you tag each one.</p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <label className="cursor-pointer text-xs font-medium px-3 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700">
              Upload files
              <input type="file" multiple className="hidden"
                onChange={(e) => { handleBulkFilesPicked(e.target.files); e.target.value = ''; }} />
            </label>
            <label className="cursor-pointer text-xs font-medium px-3 py-2 rounded-lg bg-white border border-indigo-200 text-indigo-700 hover:bg-indigo-50">
              Upload folder
              <input type="file" multiple webkitdirectory="" directory="" className="hidden"
                onChange={(e) => { handleBulkFilesPicked(e.target.files); e.target.value = ''; }} />
            </label>
          </div>
        </div>

        {/* Progress checklist */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          {/* Active deal options summary (set on previous step) */}
          {(onExtension2025 || isBusinessAcquiring || realEstateIncluded) && (
            <div className="flex flex-wrap gap-2 mb-4">
              {onExtension2025 && (
                <span className="text-xs px-2 py-1 rounded-full font-medium bg-amber-100 text-amber-700">
                  2025 on extension — 2022 returns required
                </span>
              )}
              {isBusinessAcquiring && (
                <span className="text-xs px-2 py-1 rounded-full font-medium bg-purple-100 text-purple-700">
                  Business-to-business — acquiring co. docs required
                </span>
              )}
              {realEstateIncluded && (
                <span className="text-xs px-2 py-1 rounded-full font-medium bg-teal-100 text-teal-700">
                  Real estate included — valuation required
                </span>
              )}
            </div>
          )}

          {/* Required for Term Sheet */}
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-3">
              <h3 className="text-sm font-semibold text-gray-900">Required for Term Sheet</h3>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                requiredComplete ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
              }`}>
                {requiredUploaded} / {activeRequiredDocs.length}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {activeRequiredDocs.map(d => (
                <div key={d.value} className="flex items-center gap-2 text-xs">
                  {uploadedLabels.includes(d.value) ? (
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0" />
                  ) : (
                    <div className="h-4 w-4 rounded-full border-2 border-orange-300 flex-shrink-0" />
                  )}
                  <span className={uploadedLabels.includes(d.value) ? 'text-green-700' : 'text-gray-600'}>{d.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Extra Files */}
          <div className="border-t pt-4">
            <div className="flex items-center gap-2 mb-3">
              <h3 className="text-sm font-semibold text-gray-700">Extra Files</h3>
              {extraUploaded > 0 && (
                <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-blue-100 text-blue-700">
                  {extraUploaded} uploaded
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {EXTRA_DOCS.filter(d => d.value !== 'Other').map(d => (
                <div key={d.value} className="flex items-center gap-2 text-xs">
                  {uploadedLabels.includes(d.value) ? (
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0" />
                  ) : (
                    <div className="h-4 w-4 rounded-full border-2 border-gray-200 flex-shrink-0" />
                  )}
                  <span className={uploadedLabels.includes(d.value) ? 'text-green-700' : 'text-gray-400'}>{d.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Upload form */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Upload a Document</h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Document Type</label>
              <select
                value={selectedDocType}
                onChange={(e) => setSelectedDocType(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">Select document type...</option>
                {DOC_TYPES.map(cat => (
                  <optgroup key={cat.category} label={cat.category}>
                    {cat.items.map(item => (
                      <option key={item.value} value={item.value}>{item.label}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {selectedDocType === 'Other' ? 'Document Name *' : 'Or use a custom name (overrides selection)'}
              </label>
              <input type="text" value={customLabel} onChange={(e) => setCustomLabel(e.target.value)}
                placeholder="e.g. Franchise Disclosure Document"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">File</label>
              <input
                id="broker-file-upload"
                type="file"
                onChange={(e) => setSelectedFile(e.target.files[0])}
                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
              />
            </div>

            {uploading && (
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-indigo-600 h-2 rounded-full transition-all" style={{ width: `${uploadProgress}%` }}></div>
              </div>
            )}

            {error && <p className="text-sm text-red-600">{error}</p>}
            {successMessage && <p className="text-sm text-green-600">{successMessage}</p>}

            <button
              onClick={handleUpload}
              disabled={uploading || !selectedFile || (!selectedDocType && !customLabel.trim()) || (selectedDocType === 'Other' && !customLabel.trim())}
              className="w-full py-2.5 px-4 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <ArrowUpTrayIcon className="h-5 w-5" />
              {uploading ? `Uploading... ${uploadProgress}%` : 'Upload Document'}
            </button>
          </div>

          {/* Uploaded files list */}
          {uploadedFiles.length > 0 && (
            <div className="mt-6 border-t pt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Uploaded ({uploadedFiles.length})</h4>
              <div className="space-y-1">
                {uploadedFiles.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-gray-600 bg-green-50 rounded px-3 py-1.5">
                    <CheckCircleIcon className="h-4 w-4 text-green-500" />
                    <span className="font-medium">{f.label || f.documentName}</span>
                    <span className="text-gray-400">— {f.fileName}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Share upload link */}
          {loanAppId && (
            <div className="mt-6 border-t pt-4">
              <p className="text-xs text-gray-500 mb-2">Share this link with the buyer to upload additional documents:</p>
              <div className="flex gap-2">
                <input
                  readOnly
                  value={`https://lended.ai/upload/${loanAppId}`}
                  className="flex-1 text-xs border border-gray-300 rounded-lg px-3 py-2 bg-gray-50"
                />
                <button
                  onClick={() => { navigator.clipboard.writeText(`https://lended.ai/upload/${loanAppId}`); setSuccessMessage('Link copied!'); }}
                  className="px-3 py-2 text-xs bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 font-medium"
                >
                  Copy
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bulk upload modal */}
      {bulkQueue.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Tag {bulkQueue.length} {bulkQueue.length === 1 ? 'file' : 'files'}</h3>
                <p className="text-xs text-gray-500 mt-0.5">We tried to guess each doc type from the filename — fix any that look wrong.</p>
              </div>
              <button onClick={closeBulkModal} disabled={bulkUploading} className="text-gray-400 hover:text-gray-600 disabled:opacity-30">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
              {bulkQueue.map((row, idx) => (
                <div key={idx} className={`border rounded-lg p-3 ${
                  row.status === 'done' ? 'border-green-200 bg-green-50' :
                  row.status === 'error' ? 'border-red-200 bg-red-50' :
                  row.status === 'uploading' ? 'border-indigo-200 bg-indigo-50' :
                  'border-gray-200'
                }`}>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <DocumentIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-800 truncate">{row.file.name}</span>
                      <span className="text-xs text-gray-400 flex-shrink-0">{(row.file.size / 1024).toFixed(0)} KB</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {row.status === 'done' && <CheckCircleIcon className="h-5 w-5 text-green-500" />}
                      {row.status === 'uploading' && <span className="text-xs text-indigo-600 animate-pulse">Uploading…</span>}
                      {row.status !== 'done' && row.status !== 'uploading' && (
                        <button onClick={() => removeBulkRow(idx)} disabled={bulkUploading} className="text-gray-400 hover:text-red-500 disabled:opacity-30">
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  <select
                    value={row.label}
                    onChange={(e) => updateBulkRow(idx, { label: e.target.value })}
                    disabled={bulkUploading || row.status === 'done' || !!row.customLabel}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
                  >
                    <option value="">Select document type…</option>
                    {DOC_TYPES.map(cat => (
                      <optgroup key={cat.category} label={cat.category}>
                        {cat.items.map(item => (
                          <option key={item.value} value={item.value}>{item.label}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={row.customLabel}
                    onChange={(e) => updateBulkRow(idx, { customLabel: e.target.value })}
                    placeholder="…or type a custom name (overrides above)"
                    disabled={bulkUploading || row.status === 'done'}
                    className="mt-2 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
                  />
                  {row.error && <p className="mt-2 text-xs text-red-600">{row.error}</p>}
                </div>
              ))}
            </div>
            <div className="px-6 py-4 border-t flex items-center justify-between gap-3">
              <p className="text-xs text-gray-500">
                {bulkQueue.filter(r => r.status === 'done').length} of {bulkQueue.length} uploaded
              </p>
              <div className="flex gap-2">
                <button onClick={closeBulkModal} disabled={bulkUploading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                  {bulkQueue.every(r => r.status === 'done') ? 'Done' : 'Cancel'}
                </button>
                <button onClick={startBulkUpload}
                  disabled={bulkUploading || bulkQueue.every(r => r.status === 'done') || bulkQueue.some(r => !r.label && r.status !== 'done')}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                  {bulkUploading ? 'Uploading…' : 'Upload all'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
